import type { AppUsageDayRow, AppUsageFeedRow } from '../../lib/database.types'
import { USAGE_STALE_MS } from './appUsage'
import { count } from './summaries'

/**
 * The four questions people actually ask about an app's use, answered from
 * `app_usage` -- one row per app, day, person and page.
 *
 * The desk's card (`appUsage.ts`) answers "who used what in the last thirty
 * days" as one collapsed list, summed by a view that has no day column. These
 * are the other shape of question: is use growing, has one named person opened
 * the tool, how do roles differ, which pages does nobody open. All four need
 * the days, so they read the table and the arithmetic happens here.
 *
 * Counts, never a verdict (product rule 1). Nothing here ranks a person or a
 * page into a band, scores anyone, or marks a quiet day as a problem: a
 * weekend is low because it is a weekend. The one judgement on the page is the
 * view threshold under which a page reads as little-opened, and it is a number
 * stated on the page as the display choice it is, not a line between fine and
 * not fine.
 *
 * No app, department, role or path is written here (product rule 2). The apps
 * come from the feed rows, the roles and pages come from the rows, and an app
 * that starts reporting tomorrow gets its reports with no code change.
 */

/**
 * Under this many views in the window, a page reads as one hardly anyone
 * opens. A display choice: it decides which rows the fourth report lists and
 * nothing else, and the page says so next to the list.
 */
export const LOW_USE_VIEWS = 25

/** How many of a role's pages the role report names. The rest are in the totals. */
export const ROLE_PATHS = 5

export interface UsageWindow {
  /** What the URL carries. */
  key: string
  label: string
  /** Days back from the last day with rows, or null for everything there is. */
  days: number | null
}

/**
 * How many days a visit fetches, and so the widest trailing window that can be
 * drawn without asking the server again.
 *
 * It is the default window as well, which is the point: the table grows by a
 * day of rows every day for every app that reports, and a page that reads all
 * of it to show the last month gets slower every week for nothing. The whole
 * history is still one button away, as the choice it is.
 */
export const FETCH_DAYS = 30

/**
 * The window a visit opens on, and the one the URL carries nothing for. It is
 * first in the list below, so `windowFor` falls back to it.
 */
export const DEFAULT_WINDOW: UsageWindow = { key: '30', label: 'Last 30 days', days: FETCH_DAYS }

/**
 * How much of the history a report covers. Trailing days rather than dates,
 * because the link is meant to still answer the same question next week.
 *
 * The windows are spans, not a list of anything the database holds, so naming
 * them here is not the hardcoding product rule 2 forbids -- there is no row
 * anywhere that says which spans a person might want.
 */
export const WINDOWS: ReadonlyArray<UsageWindow> = [
  DEFAULT_WINDOW,
  { key: '14', label: 'Last 14 days', days: 14 },
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: 'all', label: 'All data', days: null },
]

/** The window a URL asks for, or the default when it asks for nothing it knows. */
export function windowFor(key: string | null): UsageWindow {
  return WINDOWS.find((w) => w.key === key) ?? (WINDOWS[0] as UsageWindow)
}

/**
 * The earliest day a window needs fetched, or null for no lower bound at all.
 *
 * One span serves every trailing window: thirty days back from the day the app
 * last recorded, so picking 14 or 7 narrows what is counted out of rows already
 * held rather than asking the server again. Counted back from that last day and
 * not from today, like the windows themselves, so a sender that stopped a month
 * ago fetches its last thirty days rather than an empty thirty.
 *
 * Only "all data" returns null, and it means what it says: every row the app
 * has, because that is what was asked for.
 */
export function usageFetchFrom(win: UsageWindow, lastDay: string | null): string | null {
  if (win.days == null || !lastDay) return null
  return shiftDay(lastDay, -(FETCH_DAYS - 1))
}

/** One line of an app picker: every app that has ever reported, by its own name. */
export interface UsageAppOption {
  app: string
  name: string
}

export interface UsageDay {
  day: string
  weekday: string
  people: number
  views: number
}

export interface UsagePersonRef {
  userId: string
  name: string
  role: string
}

export interface UsagePath {
  path: string
  views: number
}

export interface PersonDay {
  day: string
  weekday: string
  views: number
  /** Most viewed first. */
  paths: UsagePath[]
}

export interface PersonReport {
  userId: string
  /** Null when no row in this app's history carries that id. */
  name: string | null
  role: string | null
  views: number
  activeDays: number
  firstDay: string | null
  lastDay: string | null
  /** Only the days they opened something, newest first. */
  days: PersonDay[]
}

export interface RoleReport {
  role: string
  people: number
  views: number
  /** The role's most-used pages, most viewed first. */
  paths: UsagePath[]
}

export interface LowUsePath {
  path: string
  views: number
  people: number
  days: number
}

export interface UsageReports {
  app: string
  name: string
  department: string
  /** When the app last reported, or null if no feed row names it. */
  asOf: string | null
  stale: boolean
  /** The earliest day this app ever recorded, whatever window is picked. */
  firstDay: string | null
  lastDay: string | null
  /** Every day in the window, oldest first, including days nothing was recorded. */
  daily: UsageDay[]
  /** Everyone the rows name, by name, for the person picker -- window or no window. */
  people: UsagePersonRef[]
  /** Null until a person is asked for. */
  person: PersonReport | null
  /** Most views first. */
  roles: RoleReport[]
  /** Least views first. */
  lowUse: LowUsePath[]
  lowUseBelow: number
  totals: { views: number; people: number; paths: number; days: number }
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * A day is a calendar date the app reported, not a moment: `2026-09-29` means
 * that Tuesday wherever it is read. So these parse it as UTC and never through
 * the reader's zone, which would slide a day either side of midnight and make
 * a Monday read as a Sunday west of Greenwich.
 */
function dayMs(day: string): number {
  return Date.parse(`${day}T00:00:00Z`)
}

/** "Tue", for the day column -- the whole reason a quiet Saturday reads as expected. */
function weekdayOf(day: string): string {
  const t = dayMs(day)
  return Number.isNaN(t) ? '' : (WEEKDAYS[new Date(t).getUTCDay()] ?? '')
}

/** "29 Sep 2026" -- a day the rows carry, written out. */
export function dayLabel(day: string | null): string {
  if (!day) return '—'
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!parts) return day
  return `${Number(parts[3])} ${MONTHS[Number(parts[2]) - 1] ?? parts[2]} ${parts[1]}`
}

function shiftDay(day: string, by: number): string {
  const t = dayMs(day)
  if (Number.isNaN(t)) return day
  return new Date(t + by * DAY_MS).toISOString().slice(0, 10)
}

/** The apps a report can be about: the feed rows, by display name. */
export function usageApps(feeds: ReadonlyArray<AppUsageFeedRow>): UsageAppOption[] {
  return feeds
    .map((f) => ({ app: f.app, name: f.display_name }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.app.localeCompare(b.app))
}

const byViews = <T extends { views: number }>(key: (t: T) => string) => (a: T, b: T) =>
  b.views - a.views || key(a).localeCompare(key(b))

export function toUsageReports(
  rows: ReadonlyArray<AppUsageDayRow>,
  feeds: ReadonlyArray<AppUsageFeedRow>,
  opts: {
    app: string
    windowDays?: number | null
    userId?: string | null
    now?: number
    /**
     * The app's earliest and latest recorded day, when the caller knows them
     * from somewhere other than these rows. The rows are a fetched slice, so
     * the history usually begins before the first of them.
     */
    firstDay?: string | null
    lastDay?: string | null
  },
): UsageReports {
  const now = opts.now ?? Date.now()
  const feed = feeds.find((f) => f.app === opts.app) ?? null
  const mine = rows.filter((r) => r.app === opts.app)

  // The first and last day of the whole history, before any window is applied.
  // The page states the first of them, and it has to come from the recorded
  // rows rather than from a feed: a feed's `window_from` is the last report's
  // own span -- a day or two -- and showing that as where the data begins
  // would make a month of history read as a tool nobody has used yet.
  //
  // Given, they come from the bounds query, which reads one row at each end
  // instead of the lot. Not given, they are scanned from what is here, which
  // is right whenever what is here is everything.
  let rowsFirst: string | null = null
  let rowsLast: string | null = null
  for (const r of mine) {
    if (!rowsFirst || r.day < rowsFirst) rowsFirst = r.day
    if (!rowsLast || r.day > rowsLast) rowsLast = r.day
  }
  const firstDay = opts.firstDay ?? rowsFirst
  const lastDay = opts.lastDay ?? rowsLast

  // A window is counted back from the last day with rows rather than from
  // today, so "last 7 days" does not empty itself the week after the sender
  // stops. That the sender has stopped is said separately, by `stale`.
  const from =
    opts.windowDays == null || !lastDay || !firstDay
      ? firstDay
      : (() => {
          const start = shiftDay(lastDay, -(opts.windowDays - 1))
          return start > firstDay ? start : firstDay
        })()
  const windowed = from ? mine.filter((r) => r.day >= from) : mine

  // Per day: how many people and how many views. Every day between the window's
  // ends is here, including the ones nothing was recorded on -- a missing row
  // and a zero are the same fact to a reader, and a series that skips the quiet
  // days reads as unbroken use.
  const dayTotals = new Map<string, { people: Set<string>; views: number }>()
  const allPeople = new Map<string, UsagePersonRef & { lastDay: string }>()
  const roles = new Map<string, { people: Set<string>; views: number; paths: Map<string, number> }>()
  const paths = new Map<string, { views: number; people: Set<string>; days: Set<string> }>()
  const personDays = new Map<string, { views: number; paths: Map<string, number> }>()
  const windowPeople = new Set<string>()
  let views = 0

  for (const r of mine) {
    // A person's name and role as the most recent row records them, so someone
    // who changed either reads as who they are now rather than twice.
    const held = allPeople.get(r.user_id)
    if (!held || r.day >= held.lastDay) {
      allPeople.set(r.user_id, { userId: r.user_id, name: r.full_name, role: r.role, lastDay: r.day })
    }
  }

  for (const r of windowed) {
    views += r.views
    windowPeople.add(r.user_id)

    const d = dayTotals.get(r.day) ?? { people: new Set<string>(), views: 0 }
    d.people.add(r.user_id)
    d.views += r.views
    dayTotals.set(r.day, d)

    const role = roles.get(r.role) ?? { people: new Set<string>(), views: 0, paths: new Map<string, number>() }
    role.people.add(r.user_id)
    role.views += r.views
    role.paths.set(r.path, (role.paths.get(r.path) ?? 0) + r.views)
    roles.set(r.role, role)

    const path = paths.get(r.path) ?? { views: 0, people: new Set<string>(), days: new Set<string>() }
    path.views += r.views
    path.people.add(r.user_id)
    path.days.add(r.day)
    paths.set(r.path, path)

    if (opts.userId && r.user_id === opts.userId) {
      const pd = personDays.get(r.day) ?? { views: 0, paths: new Map<string, number>() }
      pd.views += r.views
      pd.paths.set(r.path, (pd.paths.get(r.path) ?? 0) + r.views)
      personDays.set(r.day, pd)
    }
  }

  const daily: UsageDay[] = []
  if (from && lastDay) {
    for (let day = from; day <= lastDay; ) {
      const held = dayTotals.get(day)
      daily.push({ day, weekday: weekdayOf(day), people: held?.people.size ?? 0, views: held?.views ?? 0 })
      const next = shiftDay(day, 1)
      // A day the column could not parse leaves shiftDay returning its own
      // argument, which is the one way this loop would not end.
      if (next === day) break
      day = next
    }
  }

  const people = [...allPeople.values()]
    .map(({ userId, name, role }) => ({ userId, name, role }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.userId.localeCompare(b.userId))

  const toPaths = (counts: Map<string, number>, take?: number): UsagePath[] => {
    const list = [...counts.entries()]
      .map(([path, n]) => ({ path, views: n }))
      .sort(byViews<UsagePath>((p) => p.path))
    return take == null ? list : list.slice(0, take)
  }

  let person: PersonReport | null = null
  if (opts.userId) {
    const known = allPeople.get(opts.userId) ?? null
    const days = [...personDays.entries()]
      .map(([day, held]) => ({ day, weekday: weekdayOf(day), views: held.views, paths: toPaths(held.paths) }))
      .sort((a, b) => b.day.localeCompare(a.day))
    person = {
      userId: opts.userId,
      name: known?.name ?? null,
      role: known?.role ?? null,
      views: days.reduce((total, d) => total + d.views, 0),
      activeDays: days.length,
      // Their own first and last active day, which is the answer to "has this
      // person opened it yet" and is not the app's history.
      firstDay: days.length > 0 ? (days[days.length - 1] as PersonDay).day : null,
      lastDay: days.length > 0 ? (days[0] as PersonDay).day : null,
      days,
    }
  }

  const roleList = [...roles.entries()]
    .map(([role, held]) => ({
      role,
      people: held.people.size,
      views: held.views,
      paths: toPaths(held.paths, ROLE_PATHS),
    }))
    .sort(byViews<RoleReport>((r) => r.role))

  const lowUse = [...paths.entries()]
    .map(([path, held]) => ({ path, views: held.views, people: held.people.size, days: held.days.size }))
    .filter((p) => p.views < LOW_USE_VIEWS)
    .sort((a, b) => a.views - b.views || a.path.localeCompare(b.path))

  return {
    app: opts.app,
    name: feed?.display_name ?? opts.app,
    department: feed?.department ?? '',
    asOf: feed?.received_at ?? null,
    stale: feed ? now - Date.parse(feed.received_at) > USAGE_STALE_MS : false,
    firstDay,
    lastDay,
    daily,
    people,
    person,
    roles: roleList,
    lowUse,
    lowUseBelow: LOW_USE_VIEWS,
    totals: { views, people: windowPeople.size, paths: paths.size, days: dayTotals.size },
  }
}

/** The line under the page's head: how much use, by how many, over how long. */
export function usageTotalsLine(r: UsageReports): string {
  if (r.totals.views === 0) return 'No page views in this window'
  return [
    `${r.totals.views.toLocaleString('en-US')} views`,
    count(r.totals.people, 'person', 'people'),
    count(r.totals.paths, 'page'),
    `${count(r.totals.days, 'day')} with use`,
  ].join(' · ')
}

/** "29 Sep 2026 to 27 Oct 2026" -- what the window on screen actually spans. */
export function usageWindowLine(r: UsageReports): string {
  if (r.daily.length === 0) return 'No days in this window'
  const start = (r.daily[0] as UsageDay).day
  const end = (r.daily[r.daily.length - 1] as UsageDay).day
  return start === end ? dayLabel(start) : `${dayLabel(start)} to ${dayLabel(end)}`
}
